import React from "react";
import {
  FaLinkedin,
  FaInstagram,
  FaTwitter,
  FaGoogle,
  FaPhoneAlt,
  FaFacebookF,
  FaYoutube,
} from "react-icons/fa";
// The stacked lockup from the QuantaRock logo pack, "powered by QuantaRock"
// byline and all — the footer's brand block has the room for it that the
// header bar does not (see HeaderMain.jsx for the horizontal variant).
// .webp at 2x of the 192px it renders at, palette-quantised.
import headerLogo from "../../assets/cropped-logo.webp";
import {
  Footer_MENU_LIST,
  FOOTER_TAGLINE,
  FOOTER_CTA,
} from "../../constant/FooterData";
import { COMPANY_DATA, CERTIFICATES_DATA } from "../../constant/CompanyData";
import { Link } from "react-router-dom";
import Newsletter from "./Newsletter";
import CertSeal from "./CertSeal";

// Everything centres until `lg`, where the brand block moves alongside the
// link columns and left alignment reads better.
const linkClass =
  "block text-[var(--website-text-color)] mb-3 text-sm cursor-pointer text-center lg:text-left hover:text-[var(--website-secondary-color)] transition-colors";

// Uppercase, letter-spaced column headings — matches the corporate footer.
const headingClass =
  "text-xs font-semibold uppercase tracking-[0.18em] text-white mb-5 text-center lg:text-left";

// Icon-only links, so each needs its own aria-label: the glyph gives a screen
// reader nothing to read. Monochrome circles keep the row calm next to the CTA.
const SOCIALS = [
  {
    label: `Call ${COMPANY_DATA.phoneNumber}`,
    href: `tel:${COMPANY_DATA.phoneNumber}`,
    Icon: FaPhoneAlt,
    // mailto:/tel: hand off to a native app — a new tab would be left blank.
    sameTab: true,
  },
  {
    label: "QuantaRock on LinkedIn",
    href: COMPANY_DATA.linkedInURL,
    Icon: FaLinkedin,
  },
  {
    label: "QuantaRock on Facebook",
    href: COMPANY_DATA.facebookURL,
    Icon: FaFacebookF,
  },
  {
    label: `Email ${COMPANY_DATA.mailID}`,
    href: COMPANY_DATA.mailURL,
    Icon: FaGoogle,
    sameTab: true,
  },
  {
    label: "QuantaRock on X (Twitter)",
    href: COMPANY_DATA.twitterURL,
    Icon: FaTwitter,
  },
  {
    label: "QuantaRock on Instagram",
    href: COMPANY_DATA.instaURL,
    Icon: FaInstagram,
  },
  {
    label: "QuantaRock on YouTube",
    href: COMPANY_DATA.youtubeURL,
    Icon: FaYoutube,
  },
];

// "ISO 27001:2022" -> "ISO 27001", which is all that fits inside the seal.
const sealLabel = (title) => title.split(":")[0];

// "09/09/2028" -> "2028". The seal already names the standard, so the caption
// carries what it cannot: the version and how long it is good for.
const validTo = (expiry) => {
  const year = (expiry || "").split("/").pop();
  return /^\d{4}$/.test(year) ? `Valid to ${year}` : null;
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-[#07122d] to-[#050d20] px-6 pb-14 text-white">
      <Newsletter />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_3fr] lg:gap-12">
          {/* Brand block — full width on its own row until there is room to
              sit beside the link columns. */}
          <div className="text-center lg:pr-8 lg:text-left">
            <img
              src={headerLogo}
              alt={`${COMPANY_DATA.companyName} Logo`}
              width="384"
              height="281"
              className="mx-auto h-auto w-48 lg:mx-0"
            />

            <p className="mt-5 text-sm leading-relaxed text-[var(--website-text-color)]">
              {FOOTER_TAGLINE}
            </p>

            <address className="mt-6 space-y-2 text-sm not-italic text-[var(--website-text-color)]">
              <a
                href={COMPANY_DATA.mailURL}
                className="block hover:text-[var(--website-secondary-color)] transition-colors"
              >
                {COMPANY_DATA.mailID}
              </a>
              <a
                href={`tel:${COMPANY_DATA.phoneNumber}`}
                className="block hover:text-[var(--website-secondary-color)] transition-colors"
              >
                {COMPANY_DATA.phoneNumber}
              </a>
              <p className="leading-relaxed">{COMPANY_DATA.address}</p>
            </address>

            <div className="mt-6 flex flex-wrap justify-center gap-3 lg:justify-start">
              {SOCIALS.map(({ label, href, Icon, sameTab }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  {...(sameTab
                    ? {}
                    : { target: "_blank", rel: "noopener noreferrer" })}
                  className="flex h-10 w-10 items-center justify-center rounded-full border
                             border-white/25 text-white transition-colors
                             hover:border-[var(--website-secondary-color)]
                             hover:text-[var(--website-secondary-color)]"
                >
                  <Icon size={16} aria-hidden="true" />
                </a>
              ))}
            </div>

            <a
              href={FOOTER_CTA.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-block rounded-full bg-[var(--website-secondary-color)]
                         px-7 py-3 text-sm font-bold text-[var(--website-primary-color)]
                         transition-opacity hover:opacity-90"
            >
              {FOOTER_CTA.label}
            </a>
          </div>

          {/* Navigation columns — paired up on phones, all four across from
              `sm` up, so the footer never collapses to a single tall list. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4">
            {Footer_MENU_LIST.map((section) => (
              <div key={section.title}>
                <h3 className={headingClass}>{section.title}</h3>
                {section.links.map((link) =>
                  // Real anchors so these are crawlable and can be opened in a
                  // new tab; the QuantaRock column leaves the site entirely.
                  section.external ? (
                    <a
                      key={link.name}
                      href={link.pageId}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.pageId}
                      className={linkClass}
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Certifications strip — procurement teams look for these before
            raising a PO, so they sit in the footer of every page. */}
        <div className="mt-12 border-t border-white/15 pt-10 text-center">
          {/* This strip stays centred at every width, so it opts out of the
              shared heading's left alignment. */}
          <h3 className={`${headingClass} mb-3 lg:text-center`}>
            Certifications &amp; Compliance
          </h3>
          <p className="mx-auto max-w-2xl text-sm text-[var(--website-text-color)]">
            Certified standards that support our commitment to quality, security
            and reliable infrastructure.
          </p>

          <ul className="mt-6 flex flex-wrap items-start justify-center gap-x-10 gap-y-6">
            {CERTIFICATES_DATA.map((cert) => (
              <li key={cert.id}>
                <Link
                  to="/certifications"
                  className="group flex flex-col items-center gap-2"
                >
                  <CertSeal label={sealLabel(cert.title)} />
                  <span className="text-sm font-semibold text-white transition-colors group-hover:text-[var(--website-secondary-color)]">
                    {cert.title}
                  </span>
                  {validTo(cert.expiryDate) && (
                    <span className="text-xs text-[var(--website-text-color)]">
                      {validTo(cert.expiryDate)}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 border-t border-white/15 pt-6 text-center text-sm text-[var(--website-text-color)]">
          Copyright © {new Date().getFullYear()} - All rights reserved by{" "}
          {COMPANY_DATA.companyName}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
